import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Key, Copy, Trash2, Plus, Shield, ShieldCheck, Edit, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Display URL = the URL the user/vendor sees in the browser (e.g., myshield.com.my).
// We use this in docs/snippets so vendors integrate against the custom domain,
// not the underlying Emergent host that's baked into REACT_APP_BACKEND_URL.
const DISPLAY_URL = typeof window !== 'undefined' ? window.location.origin : BACKEND_URL;


const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function APIKeysPage({ user, onLogout }) {
  const [keys, setKeys] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyOrgId, setNewKeyOrgId] = useState('');
  const [newKeyIpWhitelist, setNewKeyIpWhitelist] = useState(false);
  const [newKeyAllowedIps, setNewKeyAllowedIps] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchKeys = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api-keys`, { headers: getAuthHeaders() });
      setKeys(response.data);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/organizations`, { headers: getAuthHeaders() });
      setOrganizations(response.data || []);
    } catch (error) {
      // Expected for non-super-admin roles: /organizations is 403 for them.
      // Anything else is worth seeing in the console while debugging.
      if (error.response?.status !== 403) {
        console.error('Failed to load organizations for API key assignment:', error);
      }
      setOrganizations([]);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin, fetchKeys, fetchOrganizations]);

  const createKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }
    if (isSuperAdmin && !newKeyOrgId) {
      toast.error('Please select an organization to assign this key to');
      return;
    }

    setCreatingKey(true);
    try {
      const allowedIps = newKeyAllowedIps
        .split('\n')
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);

      const payload = {
        name: newKeyName,
        ip_whitelist_enabled: newKeyIpWhitelist,
        allowed_ips: allowedIps
      };
      if (isSuperAdmin) payload.organization_id = newKeyOrgId;

      const response = await axios.post(`${API}/api-keys`, payload, { headers: getAuthHeaders() });

      setKeys([...keys, response.data]);
      setNewKeyName('');
      setNewKeyOrgId('');
      setNewKeyIpWhitelist(false);
      setNewKeyAllowedIps('');
      setDialogOpen(false);
      toast.success('API key created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const openEditDialog = (key) => {
    setEditingKey({
      ...key,
      allowed_ips_text: (key.allowed_ips || []).join('\n')
    });
    setEditDialogOpen(true);
  };

  const updateKey = async (e) => {
    e.preventDefault();
    if (!editingKey) return;

    try {
      const allowedIps = editingKey.allowed_ips_text
        .split('\n')
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);

      const payload = {
        name: editingKey.name,
        ip_whitelist_enabled: editingKey.ip_whitelist_enabled,
        allowed_ips: allowedIps,
        is_active: editingKey.is_active,
      };
      // Only super_admin can reassign the org (backend also enforces this)
      if (isSuperAdmin && editingKey.organization_id) {
        payload.organization_id = editingKey.organization_id;
      }

      await axios.put(`${API}/api-keys/${editingKey.id}`, payload, { headers: getAuthHeaders() });

      // Update local state with the reassigned org name lookup
      const reassignedOrg = organizations.find(o => o.id === editingKey.organization_id);
      setKeys(keys.map(k => k.id === editingKey.id
        ? { ...k, ...payload, organization_name: reassignedOrg?.name || k.organization_name }
        : k));
      setEditDialogOpen(false);
      setEditingKey(null);
      toast.success('API key updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update API key');
    }
  };

  const deleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;

    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { headers: getAuthHeaders() });
      setKeys(keys.filter((k) => k.id !== keyId));
      toast.success('API key deleted');
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('API key copied to clipboard');
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('API key copied to clipboard');
      } catch (err) {
        toast.error('Failed to copy. Please copy manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="api-keys">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="api-keys-title">
              API Keys
            </h1>
            <p className="text-sm text-slate-600 mt-2">Manage API keys for external system integration</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="create-key-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Key
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New API Key</DialogTitle>
              </DialogHeader>
              <form onSubmit={createKey} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="key-name">Key Name</Label>
                  <Input
                    id="key-name"
                    placeholder="Production API Key"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="mt-2"
                    data-testid="input-key-name"
                  />
                </div>

                {isSuperAdmin && (
                  <div>
                    <Label htmlFor="key-org">Assign to Organization</Label>
                    <p className="text-xs text-slate-500 mb-2">All scans made with this key will be billed to and tracked for the selected organization.</p>
                    <select
                      id="key-org"
                      value={newKeyOrgId}
                      onChange={(e) => setNewKeyOrgId(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                      required
                      data-testid="select-key-org"
                    >
                      <option value="">— Select an organization —</option>
                      {organizations.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.name} ({o.id.slice(0, 8)}…)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-md">
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-slate-600 mr-2" />
                    <div>
                      <Label htmlFor="ip-whitelist">Enable IP Whitelisting</Label>
                      <p className="text-xs text-slate-500">Restrict API access to specific IP addresses</p>
                    </div>
                  </div>
                  <Switch
                    id="ip-whitelist"
                    checked={newKeyIpWhitelist}
                    onCheckedChange={setNewKeyIpWhitelist}
                  />
                </div>

                {newKeyIpWhitelist && (
                  <div>
                    <Label htmlFor="allowed-ips">Allowed IP Addresses</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Enter one IP address or CIDR range per line (e.g., 192.168.1.100 or 10.0.0.0/8)
                    </p>
                    <textarea
                      id="allowed-ips"
                      className="w-full h-24 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                      placeholder="192.168.1.100&#10;10.0.0.0/8&#10;172.16.0.0/12"
                      value={newKeyAllowedIps}
                      onChange={(e) => setNewKeyAllowedIps(e.target.value)}
                    />
                  </div>
                )}

                <Button type="submit" disabled={creatingKey} className="w-full" data-testid="submit-create-key">
                  {creatingKey ? 'Creating...' : 'Create Key'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit API Key</DialogTitle>
            </DialogHeader>
            {editingKey && (
              <form onSubmit={updateKey} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="edit-key-name">Key Name</Label>
                  <Input
                    id="edit-key-name"
                    value={editingKey.name}
                    onChange={(e) => setEditingKey({...editingKey, name: e.target.value})}
                    className="mt-2"
                  />
                </div>

                {isSuperAdmin && (
                  <div>
                    <Label htmlFor="edit-key-org">Assigned Organization</Label>
                    <p className="text-xs text-slate-500 mb-2">Reassign this key to a different organization. All future scans will be billed to the selected org.</p>
                    <select
                      id="edit-key-org"
                      value={editingKey.organization_id || ''}
                      onChange={(e) => setEditingKey({...editingKey, organization_id: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                      data-testid="edit-select-key-org"
                    >
                      <option value="">— Unassigned (not recommended) —</option>
                      {organizations.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.name} ({o.id.slice(0, 8)}…)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-md">
                  <div className="flex items-center">
                    <Globe className="w-4 h-4 text-slate-600 mr-2" />
                    <div>
                      <Label>Key Status</Label>
                      <p className="text-xs text-slate-500">Enable or disable this API key</p>
                    </div>
                  </div>
                  <Switch
                    checked={editingKey.is_active}
                    onCheckedChange={(checked) => setEditingKey({...editingKey, is_active: checked})}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-md">
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-slate-600 mr-2" />
                    <div>
                      <Label>IP Whitelisting</Label>
                      <p className="text-xs text-slate-500">Restrict access to specific IPs</p>
                    </div>
                  </div>
                  <Switch
                    checked={editingKey.ip_whitelist_enabled}
                    onCheckedChange={(checked) => setEditingKey({...editingKey, ip_whitelist_enabled: checked})}
                  />
                </div>

                {editingKey.ip_whitelist_enabled && (
                  <div>
                    <Label>Allowed IP Addresses</Label>
                    <p className="text-xs text-slate-500 mb-2">One IP or CIDR per line</p>
                    <textarea
                      className="w-full h-24 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono"
                      value={editingKey.allowed_ips_text}
                      onChange={(e) => setEditingKey({...editingKey, allowed_ips_text: e.target.value})}
                    />
                  </div>
                )}

                <Button type="submit" className="w-full">Save Changes</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Core System Integration Info */}
        <Card className="border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-start">
            <ShieldCheck className="w-5 h-5 text-emerald-600 mr-3 mt-0.5" />
            <div>
              <h3 className="font-semibold text-emerald-900 mb-2">Core System Integration API (v1)</h3>
              <code className="text-xs font-mono bg-white px-3 py-2 rounded border border-emerald-200 block mb-2">
                POST {DISPLAY_URL}/api/v1/screen/customer
              </code>
              <code className="text-xs font-mono bg-white px-3 py-2 rounded border border-emerald-200 block">
                POST {DISPLAY_URL}/api/v1/screen/file (CSV/Excel upload)
              </code>
              <p className="text-sm text-emerald-700 mt-3">
                Full KYC screening with IP whitelisting support. See <a href="/documentation" className="underline text-blue-600">integration guide</a> for details.
              </p>
            </div>
          </div>
        </Card>

        {/* API Keys List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading API keys...</div>
        ) : keys.length === 0 ? (
          <Card className="p-12 text-center border border-slate-200">
            <Key className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No API keys yet. Create one to integrate with your systems.</p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="api-keys-list">
            {keys.map((key) => (
              <Card key={key.id} className={`border shadow-sm ${key.is_active === false ? 'border-rose-200 bg-rose-50/50' : 'border-slate-200'}`} data-testid={`api-key-card-${key.id}`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <Key className="w-4 h-4 text-slate-400 mr-2" />
                        <h3 className="font-semibold text-slate-900">{key.name}</h3>
                        {key.is_active === false && (
                          <span className="ml-2 px-2 py-0.5 bg-rose-100 text-rose-700 text-xs rounded">Disabled</span>
                        )}
                        {key.ip_whitelist_enabled && (
                          <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded flex items-center">
                            <Shield className="w-3 h-3 mr-1" />
                            IP Restricted
                          </span>
                        )}
                      </div>
                      {key.organization_id && (
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded text-xs" data-testid={`org-badge-${key.id}`}>
                          <span className="text-indigo-700 font-medium">Org:</span>
                          <span className="text-indigo-900">{key.organization_name || 'Unknown'}</span>
                          <code className="text-[10px] text-indigo-500 font-mono">({key.organization_id.slice(0, 8)}…)</code>
                        </div>
                      )}
                      <p className="text-xs font-mono text-slate-500 mt-2">
                        Created: {new Date(key.created_at).toLocaleDateString()}
                      </p>
                      {key.last_used && (
                        <p className="text-xs font-mono text-slate-500">
                          Last used: {new Date(key.last_used).toLocaleString()}
                        </p>
                      )}
                      {key.ip_whitelist_enabled && key.allowed_ips && key.allowed_ips.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Allowed IPs: {key.allowed_ips.slice(0, 3).join(', ')}{key.allowed_ips.length > 3 && ` +${key.allowed_ips.length - 3} more`}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(key)}
                        className="text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteKey(key.id)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-key-${key.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* API Key Display */}
                  <div className="bg-slate-900 p-4 rounded-md flex items-center justify-between">
                    <code className="text-sm font-mono text-white flex-1 truncate" data-testid={`key-value-${key.id}`}>
                      {key.key}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(key.key)}
                      className="text-white hover:bg-slate-800 ml-2"
                      data-testid={`copy-key-${key.id}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Usage Example */}
        <Card className="border border-slate-200 shadow-sm">
          <div className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Example: Customer Screening API</h3>
            <p className="text-xs text-slate-500 mb-4">
              Only <code className="font-mono text-slate-700">full_name</code> is required. All other fields are optional and improve match accuracy.
              <code className="font-mono text-slate-700"> transaction_type</code> can be any string (e.g. <code className="font-mono">remittance</code>, <code className="font-mono">currency_exchange</code>, <code className="font-mono">account_opening</code>) or omitted entirely.
            </p>
            <pre className="bg-slate-900 text-white p-4 rounded-md text-xs font-mono overflow-x-auto">
              {`curl -X POST "${DISPLAY_URL}/api/v1/screen/customer" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "full_name": "John Smith",
    "customer_id": "CUST-001",
    "date_of_birth": "1985-03-15",
    "nationality": "US",
    "gender": "male",
    "id_type": "passport",
    "id_number": "AB123456"
  }'`}
            </pre>
          </div>
        </Card>
      </div>
    </Layout>
  );
}